import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './Icon';

/**
 * 번호 페이지 넘김 (컨셉 §7.5 · 루브릭 R1-9).
 *
 * 「‹ 이전 · 1 … 5 6 [7] 8 9 … 52 · 다음 ›」 + 「페이지 이동 [ ] / 52」.
 * 600px 이하에서는 「‹ [7] / 52 ›」 — 가운데 숫자 칸이 곧 페이지 이동 입력이다.
 * href 를 주면 각 번호가 주소 링크(새 탭 · 공유 가능), 없으면 버튼 + onChange.
 */
interface Props {
  page: number;
  /** pages 를 직접 주거나, pageSize + total 로 계산한다 */
  pages?: number;
  pageSize?: number;
  total?: number;
  onChange?: (page: number) => void;
  href?: (page: number) => string;
  /** 페이지가 바뀐 뒤 보낼 목록 이름 (화면 낭독기 · aria-label) */
  label?: string;
}

/** 1 … 5 6 [7] 8 9 … 52 — 처음 · 끝 · 현재 ±2. 빈 곳이 한 칸뿐이면 「…」 대신 그 번호. */
export function pageItems(cur: number, pages: number): Array<number | 'gap'> {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const keep = new Set<number>([1, pages]);
  const lo = cur <= 4 ? 1 : cur - 2;
  const hi = cur >= pages - 3 ? pages : cur + 2;
  for (let p = Math.max(1, Math.min(lo, pages - 4)); p <= Math.min(pages, Math.max(hi, 5)); p++) keep.add(p);
  const sorted = [...keep].sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  sorted.forEach((p, i) => {
    const prev = sorted[i - 1];
    if (prev !== undefined && p - prev === 2) out.push(prev + 1);
    else if (prev !== undefined && p - prev > 2) out.push('gap');
    out.push(p);
  });
  return out;
}

export default function Pagination({ page, pages: pagesProp, pageSize, total, onChange, href, label = '페이지 넘김' }: Props) {
  const pages = pagesProp ?? Math.max(1, Math.ceil((total ?? 0) / (pageSize || 1)));
  const jumpId = useId();
  const [jump, setJump] = useState('');
  const [compact, setCompact] = useState(String(page));
  useEffect(() => { setCompact(String(page)); setJump(''); }, [page]);

  if (pages <= 1) return null;

  const go = (raw: string) => {
    const n = Math.round(Number(raw));
    if (!Number.isFinite(n) || n < 1) return;
    const p = Math.min(pages, n);
    if (p !== page) onChange?.(p);   // 링크 방식에서도 입력 이동은 onChange 가 주소를 바꾼다
  };

  const Item = ({ p, children, cls, aria }: { p: number; children: React.ReactNode; cls: string; aria: string }) =>
    href
      ? <Link className={cls} to={href(p)} aria-label={aria}>{children}</Link>
      : <button type="button" className={cls} aria-label={aria} onClick={() => onChange?.(p)}>{children}</button>;

  const prev = page > 1
    ? <Item p={page - 1} cls="btn sm pager-step" aria="이전 페이지"><Icon name="chevron-left" size={16} /><span className="pager-step-text">이전</span></Item>
    : <span className="btn sm pager-step disabled" aria-disabled="true"><Icon name="chevron-left" size={16} /><span className="pager-step-text">이전</span></span>;
  const next = page < pages
    ? <Item p={page + 1} cls="btn sm pager-step" aria="다음 페이지"><span className="pager-step-text">다음</span><Icon name="chevron-right" size={16} /></Item>
    : <span className="btn sm pager-step disabled" aria-disabled="true"><span className="pager-step-text">다음</span><Icon name="chevron-right" size={16} /></span>;

  return (
    <nav className="pager" aria-label={label}>
      {prev}
      <ol className="pager-nums">
        {pageItems(page, pages).map((p, i) => (
          <li key={p === 'gap' ? `g${i}` : p}>
            {p === 'gap' ? <span className="pager-gap" aria-hidden="true">…</span>
              : p === page
                ? <span className="pager-num on" aria-current="page" aria-label={`${p}쪽, 현재 쪽`}>{p}</span>
                : <Item p={p} cls="pager-num" aria={`${p}쪽`}>{p}</Item>}
          </li>
        ))}
      </ol>
      {/* ≤600: 「‹ [7] / 52 ›」 — 숫자 칸에 쳐서 이동 */}
      <form className="pager-compact" onSubmit={(e) => { e.preventDefault(); go(compact); }}>
        <input type="number" inputMode="numeric" min={1} max={pages} value={compact}
               aria-label={`페이지 번호 — 전체 ${pages.toLocaleString()}쪽 중`}
               onChange={(e) => setCompact(e.target.value)} onBlur={() => go(compact)} />
        <span aria-hidden="true">/ {pages.toLocaleString()}</span>
      </form>
      {next}
      <form className="pager-jump" onSubmit={(e) => { e.preventDefault(); go(jump); }}>
        <label htmlFor={jumpId}>페이지 이동</label>
        <input id={jumpId} type="number" inputMode="numeric" min={1} max={pages} value={jump}
               placeholder={String(page)} onChange={(e) => setJump(e.target.value)} />
        <span>/ {pages.toLocaleString()}</span>
        <button type="submit" className="btn sm"><Icon name="chevron-right" size={16} /> 이동</button>
      </form>
    </nav>
  );
}
